import { ListCard, FollowersChange } from "@/components/shared";
import type { BaseListItem } from "@/types";

type RankItem = BaseListItem & { rank: number };
type RankItemWithChanges = RankItem & {
  rank_1d?: number | null;
  rank_7d?: number | null;
  rank_30d?: number | null;
};

interface RankCardProps<T extends RankItem> {
  item: T;
  onCardClick: (appid: number) => void;
  primaryColor: string;
}

export function RankCard<T extends RankItem>({
  item,
  onCardClick,
  primaryColor,
}: RankCardProps<T>) {
  const hasRankChanges = 
    "rank_1d" in item || 
    "rank_7d" in item || 
    "rank_30d" in item;
  
  const rankItem = item as RankItemWithChanges;

  return (
    <ListCard
      item={item}
      onCardClick={onCardClick}
      renderImage={(item) => ({
        src: item.capsule_image,
        alt: item.name,
        width: 130,
        height: 49,
      })}
      renderTitle={(item) => item.name}
      renderPrice={(item) => ({
        isFree: item.is_free,
        priceFinal: item.price_final,
        priceInitial: item.price_initial,
        priceDiscount: item.price_discount,
      })}
      renderPlatforms={(item) => ({
        supportsWindows: item.supports_windows,
        supportsMac: item.supports_mac,
        supportsLinux: item.supports_linux,
      })}
      renderBadge={(item) => (
        <div
          className="flex items-center justify-center bg-[#1b2838] border border-[#2a475e] rounded px-3 font-bold text-lg min-w-[50px]"
          style={{ color: primaryColor }}
        >
          #{item.rank}
        </div>
      )}
      renderMetadata={(item) => (
        <>
          <span>ID: {item.appid}</span>
          {item.release_date && (
            <>
              <span className="text-[#2a475e] mx-1">•</span>
              <span>{item.release_date}</span>
            </>
          )}
        </>
      )}
      renderFooter={
        hasRankChanges
          ? () => (
              <div className="flex items-center justify-between gap-4 mt-2 pt-2 border-t border-[#2a475e]">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-[#8f98a0]">1D</span>
                  <FollowersChange value={rankItem.rank_1d ?? null} />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-[#8f98a0]">7D</span>
                  <FollowersChange value={rankItem.rank_7d ?? null} />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-[#8f98a0]">30D</span>
                  <FollowersChange value={rankItem.rank_30d ?? null} />
                </div>
              </div>
            )
          : undefined
      }
    />
  );
}

